import axios from 'axios';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Button, CircularProgress, Container, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';

import Logo from '../components/logo';

import { API_BASE_URL } from '../utils/env';

// ----------------------------------------------------------------------
const StyledRoot = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const StyledContent = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  gap: '20px',
  padding: theme.spacing(8, 0),
}));

// ----------------------------------------------------------------------

// Email validation function
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleForgotPassword = async () => {
    if (!isValidEmail(email)) {
      toast.error('Please enter a valid email address');
      setError('Please enter a valid email address');
      setTimeout(() => {
        setError(null);
      }, 3000);
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/Account/forgot-password?email=${encodeURIComponent(email)}`);
      if (response.status === 204) {
        localStorage.setItem('resetEmail', email);
        toast.success('A reset link has been sent to your email address');
        navigate('/reset-password');
      } else {
        toast.error('Failed to send reset link. Please try again later');
      }
    } catch (error) {
      toast.error('An error occurred. Please try again later');
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <Helmet>
        <title> Forgot Password | CaaS </title>
      </Helmet>

      <StyledRoot>
        <Logo
          sx={{
            position: 'fixed',
            top: { xs: 16, sm: 24, md: 40 },
            left: { xs: 16, sm: 24, md: 40 },
          }}
        />

        <Container maxWidth="sm">
          <StyledContent>
            <Typography>
              <Typography variant="h3" gutterBottom sx={{ textAlign: 'center', color: '#000080' }}>
                Forgot Password
              </Typography>
              <Typography m={0} variant="subtitle1" color={'grey'} textAlign={'center'}>
                Kindly enter your email address to get a reset link
              </Typography>
            </Typography>
            <TextField
              sx={{
                // change the focus color
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#000080',
                },
                // the label color
                '& .MuiInputLabel-outlined.Mui-focused': {
                  color: '#000080',
                },
              }}
              fullWidth
              name="email"
              label="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              error={error !== null}
              className={error !== null ? 'shaky' : ''}
            />
            <Button
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              onClick={handleForgotPassword}
              sx={{
                backgroundColor: '#000080',

                '&:hover': {
                  backgroundColor: '#000090',
                },
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Send'}
            </Button>
            {/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <Typography>
              <span
                style={{ fontWeight: '700', textAlign: 'center', cursor: 'pointer' }}
                onClick={() => navigate('/login')}
              >
                Click here
              </span>{' '}
              to Login
            </Typography>
            {/* eslint-enable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          </StyledContent>
        </Container>
      </StyledRoot>
    </>
  );
}
